/**
 * Two Sense — labeau_api service client.
 *
 * The labeau backend exposes a public, paginated endpoint:
 *   GET https://api.labeaupos.com/service/all_service_list/?shop=<id>
 *
 * It returns DRF-style {count, next, previous, results} payloads. We follow
 * the `next` cursor until exhausted so the UI receives the full catalogue in
 * one promise.
 */

/* ── API host & shop identity ──
   Per-environment endpoints live in src/utils/constant.js, mirroring
   labeau_dashboard's constant.js. Switch environments there — never here. */
import { API_BASE_URL, TWO_SENSE_SHOP_ID, DEFAULT_PAGE_SIZE, SHOP_CURRENCY_DECIMALS, CURRENCY_LOCALE } from "../utils/constant";

export { API_BASE_URL, TWO_SENSE_SHOP_ID, SHOP_CURRENCY_DECIMALS };

/**
 * Fetch every page of services for the given shop and return a flat array.
 * If the network fails partway, the partial results gathered so far are
 * returned so the page can still render *something* useful.
 */
export async function fetchAllServices({ shopId = TWO_SENSE_SHOP_ID, signal, pageSize = DEFAULT_PAGE_SIZE } = {}) {
    const collected = [];
    let url = `${API_BASE_URL}/service/all_service_list/?shop=${encodeURIComponent(shopId)}&page_size=${pageSize}&ordering=name`;

    while (url) {
        const res = await fetch(url, {
            method: "GET",
            headers: { Accept: "application/json" },
            signal,
        });

        if (!res.ok) {
            throw new Error(`Service request failed (${res.status})`);
        }

        const json = await res.json();
        if (Array.isArray(json.results)) {
            collected.push(...json.results);
        } else if (Array.isArray(json)) {
            // Just in case the endpoint is ever returned without pagination.
            collected.push(...json);
            break;
        }

        url = json.next || null;
    }

    return collected;
}

/**
 * Submit a public website appointment booking via the dedicated external-
 * booking endpoint. Creates a pending appointment (source=WEBSITE) in the
 * shop's Incoming queue for staff to confirm. Payload:
 *   { shop, service_ids:[id], name, phone_code:id, contact, date:"YYYY-MM-DD",
 *     start:"<UTC ISO>", remark }
 * Throws an Error (with the server's message when available) on failure.
 */
export async function createPublicAppointment(payload, { signal } = {}) {
    const res = await fetch(`${API_BASE_URL}/appointment/booking/website`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
        signal,
    });
    let json = null;
    try {
        json = await res.json();
    } catch (e) {
        /* non-JSON response */
    }
    if (!res.ok || (json && json.success === false)) {
        throw new Error((json && json.message) || `Booking failed (${res.status})`);
    }
    return json;
}

/**
 * Fetch the list of international dialing codes. Each entry looks like:
 *   { id, country: { id, name, code }, code: "+60" }
 */
export async function fetchPhoneCodes({ signal } = {}) {
    const res = await fetch(`${API_BASE_URL}/api/phone_code_list/`, {
        method: "GET",
        headers: { Accept: "application/json" },
        signal,
    });
    if (!res.ok) {
        throw new Error(`Phone code request failed (${res.status})`);
    }
    const json = await res.json();
    return Array.isArray(json) ? json : json.results || [];
}

/**
 * Convenience: fetch active categories for the same shop, derived from the
 * service list itself. The dedicated category endpoint requires auth, but the
 * services payload already carries `service.category`, so we project unique
 * (id, name) pairs from there.
 */
export function deriveCategories(services) {
    const seen = new Map();
    for (const s of services) {
        const c = s?.category;
        if (!c || !c.id || !c.name) continue;
        if (!seen.has(c.id)) {
            seen.set(c.id, { id: c.id, name: c.name, count: 0 });
        }
        seen.get(c.id).count += 1;
    }
    return Array.from(seen.values()).sort((a, b) => b.count - a.count);
}

/**
 * Drop anything the shop has explicitly hidden from members.
 *
 * Visibility is enforced SERVER-side: `all_service_list` filters on
 * `member_visible=True`, and its member-safe serializer
 * (`ServiceSerializersMember`) deliberately does NOT return the field — a
 * customer-facing payload has no reason to carry it. So this must only reject
 * an explicit `false`; testing `=== true` treated every record's *absent* field
 * as hidden and silently emptied the whole catalogue.
 */
export function filterMemberVisible(services) {
    if (!Array.isArray(services)) return [];
    return services.filter((s) => s?.member_visible !== false);
}

/**
 * Pretty-print a price with thousands separators, showing the number of
 * decimal places the shop's currency uses (fixed, so 30 → "30.00").
 */
export function formatPrice(value, decimals = SHOP_CURRENCY_DECIMALS) {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return "—";
    }
    const d = Number.isFinite(decimals) ? Math.max(0, Math.trunc(decimals)) : 2;
    return new Intl.NumberFormat(CURRENCY_LOCALE, {
        minimumFractionDigits: d,
        maximumFractionDigits: d,
    }).format(Number(value));
}
